/**
 * O que o cartão da criação de ambiente mostra antes (e depois) do Confirmar:
 * serviços com porta e host, banco, CloudHSM (quando há), rede, conta e a
 * proteção com que o ambiente nasce — WAF e ZTNA (spec 2026-10-09 §4 F1.6,
 * consoleai#26063, consoleai#26220).
 *
 * O dado vem pronto do servidor (`cartao_do_pedido.resumo_da_criacao`, a
 * mesma entrada do gerador) — aqui só se dispõe. Rótulos em Barlow, dado em
 * JetBrains Mono (`Dado`), tudo alinhado à esquerda.
 */
import { Group, Stack, Text } from '@mantine/core'
import { Dado } from './Dado'

export interface ServicoDoResumo {
  nome: string
  porta: number
  host: string | null
  exposicao: string
  tipo_instancia: string
  /** Frontend atrás do ZTNA (consoleai#26334): o `host` é o nome que o ZTNA publica. */
  pelo_ztna?: boolean
  /** Só em quem divide máquina com outros serviços (`instancia` do rascunho). */
  instancia?: string
}

export interface ResumoDaCriacaoDados {
  projeto: string
  ambiente: string
  conta: string
  regiao: string
  dominio: string
  servicos: ServicoDoResumo[]
  banco: {
    engine: string
    engine_version?: string
    instance_class?: string
    storage_gb?: number
    multi_az?: string
    /** Databases que não são de serviço nenhum (consoleai#26334). */
    databases_extras?: string[]
  }
  /** Só quando o stage tem CloudHSM (consoleai#26220). */
  cloudhsm?: CloudHsmDoResumo
  rede: { vpc: string }
  /** Ausente no resumo gravado antes de consoleai#26063. */
  waf?: { gerenciados: 'bloqueio' | 'contagem'; no_alb_interno: boolean }
}

export interface CloudHsmDoResumo {
  modo: string
  hsms: number
  hsm_type: string
  custo_mensal: { mensal: number | null; preco_hora: number | null; ressalva: string }
}

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }

function Rotulo({ children }: { children: React.ReactNode }) {
  return <Text size="xs" c="dimmed" style={BARLOW}>{children}</Text>
}

function textoDoBanco(b: ResumoDaCriacaoDados['banco']): string {
  if (b.engine === 'nenhum') return 'sem banco'
  return [
    [b.engine, b.engine_version].filter(Boolean).join(' '),
    b.instance_class,
    b.storage_gb != null ? `${b.storage_gb} GB` : null,
    // consoleai#25979: o módulo do banco não lê multi_az; prometer aqui seria mentir.
    b.multi_az === 'sim' ? 'multi-AZ pedido, ainda não aplicado' : null,
    b.databases_extras?.length ? `databases extras: ${b.databases_extras.join(', ')}` : null,
  ].filter(Boolean).join(' · ')
}

const USD = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' })

function textoDoCloudHsm(c: CloudHsmDoResumo): string {
  const custo = c.custo_mensal.mensal != null
    ? `${USD.format(c.custo_mensal.mensal)}/mês`
    : 'custo não estimado'
  return [`${c.modo} · ${c.hsms} HSMs ${c.hsm_type}`, custo].join(' · ')
}

export function ResumoDaCriacao({ resumo }: { resumo: ResumoDaCriacaoDados }) {
  return (
    <Stack gap={6} mb="xs">
      <Stack gap={2}>
        <Rotulo>{`Serviços (${resumo.servicos.length})`}</Rotulo>
        <Stack component="ul" gap={2} aria-label="Serviços do ambiente"
               style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {resumo.servicos.map((s) => (
            <li key={s.nome}>
              <Group gap={6} wrap="wrap">
                <Text size="sm" fw={600} style={BARLOW}>{s.nome}</Text>
                <Dado>{`:${s.porta}`}</Dado>
                {s.host && <Dado>{s.host}</Dado>}
                {s.pelo_ztna && (
                  <Text size="xs" c="dimmed" style={BARLOW}>publicado pelo ZTNA</Text>
                )}
                <Text size="xs" c="dimmed" style={BARLOW}>{s.exposicao}</Text>
                {s.instancia && (
                  <Text size="xs" c="dimmed" style={BARLOW}>{`na máquina ${s.instancia}`}</Text>
                )}
              </Group>
            </li>
          ))}
        </Stack>
      </Stack>
      <Group gap={6} wrap="wrap">
        <Rotulo>Banco</Rotulo>
        <Dado>{textoDoBanco(resumo.banco)}</Dado>
      </Group>
      {resumo.cloudhsm && (
        <Group gap={6} wrap="wrap">
          <Rotulo>CloudHSM</Rotulo>
          <Dado>{textoDoCloudHsm(resumo.cloudhsm)}</Dado>
          <Text size="xs" c="dimmed" style={BARLOW}>nasce sem inicializar; ativar é manual</Text>
        </Group>
      )}
      <Group gap={6} wrap="wrap">
        <Rotulo>Rede</Rotulo>
        <Dado>{resumo.rede.vpc}</Dado>
      </Group>
      {resumo.waf && (
        <Group gap={6} wrap="wrap">
          <Rotulo>WAF</Rotulo>
          <Dado>
            {`grupos gerenciados em ${resumo.waf.gerenciados}`}
          </Dado>
          <Text size="xs" c="dimmed" style={BARLOW}>
            {resumo.waf.no_alb_interno ? 'no ALB público e no interno' : 'no ALB público'}
          </Text>
        </Group>
      )}
      <Group gap={6} wrap="wrap">
        <Rotulo>Conta</Rotulo>
        <Dado>{resumo.conta}</Dado>
        <Dado>{resumo.regiao}</Dado>
      </Group>
    </Stack>
  )
}
